import React from 'react'
import {
    getPitchTypeColor,
    getPitchTypeColorGrouping,
} from '../utils/pitch-type-colors'
import {
    asHandedness,
    getLeagueCurveKey,
    parseUniqueId,
} from './key-parsing.ts'
import getDistributionColor from './utils/get-distribution-color.ts'
import getShortPitchName from '../utils/get-short-pitch-name.ts'
import { getPitchNameGrouping } from '../utils/pitch-names'
import type { PitchTypeColorMode } from './utils/can-use-pitch-type-colors.ts'

type LeagueAverageLegendItemProps = {
    uniqueId: string
    groupBys: string[]
    pitchTypeColors: boolean
    pitchTypeColorMode: PitchTypeColorMode
    implicitPitchHand: 'L' | 'R' | 'X'
    implicitBatSide: 'L' | 'R' | 'X'
}

export default function LeagueAverageLegendItem(
    props: LeagueAverageLegendItemProps
) {
    const {
        uniqueId,
        groupBys,
        pitchTypeColors,
        pitchTypeColorMode,
        implicitPitchHand,
        implicitBatSide,
    } = props
    const unique = parseUniqueId(uniqueId, groupBys)
    const pitchType03 =
        typeof unique.api_pitch_type_group03 === 'string'
            ? unique.api_pitch_type_group03
            : null
    const pitchType09 =
        typeof unique.api_pitch_type_group09 === 'string'
            ? unique.api_pitch_type_group09
            : null
    const hasGroup09 = groupBys.includes('api_pitch_type_group09')
    const hasGroup03 = groupBys.includes('api_pitch_type_group03')
    let pitchTypeForAverage = pitchType03 ?? pitchType09

    if (hasGroup03) {
        pitchTypeForAverage = pitchType03
    } else if (hasGroup09) {
        pitchTypeForAverage = pitchType09
    }

    const pitchTypeForColor =
        pitchTypeColorMode === 'group09' ? pitchType09 : pitchType03
    const pitchHand = asHandedness(implicitPitchHand)
    const batSide = asHandedness(implicitBatSide)
    const year = typeof unique.year === 'number' ? unique.year : null
    const key = getLeagueCurveKey({
        pitchType: pitchTypeForAverage,
        pitchHand,
        batSide,
        year,
    })
    let color = getDistributionColor(key)

    if (pitchTypeColors && pitchTypeForColor !== null) {
        color =
            pitchTypeColorMode === 'group09'
                ? getPitchTypeColorGrouping(pitchTypeForColor)
                : getPitchTypeColor(pitchTypeForColor)
    }

    const style = {
        '--league-average-color': color,
    } as React.CSSProperties

    const pitchHandFormat = pitchHand !== 'X' ? `${pitchHand}HP` : null
    const batFormat = batSide !== 'X' ? `${batSide}HB` : null
    let pitchTypeFormat = null

    if (pitchTypeForAverage) {
        pitchTypeFormat = hasGroup09
            ? getPitchNameGrouping(pitchTypeForAverage)
            : getShortPitchName(pitchTypeForAverage)
    }

    const avgGroupBys = [pitchHand, batSide, year, pitchTypeForAverage].filter(
        (d) => d
    )
    if (avgGroupBys.length > 2) {
        const size = Math.max(14 - avgGroupBys.length, 10)
        style.fontSize = `${size}px`
    }

    const fullLabel = [
        year,
        'MLB Average',
        pitchHandFormat,
        batFormat,
        pitchTypeFormat,
    ]
        .filter(Boolean)
        .join(' ')

    const isAll =
        pitchHand === 'X' && batSide === 'X' && !year && !pitchTypeForAverage

    const label = isAll ? 'MLB Average (All Pitches)' : fullLabel

    return (
        <div className="swing-timing-league-average" style={style}>
            <span className="swing-timing-league-average-label">{label}</span>
        </div>
    )
}
